import { TranslateService } from '@ngx-translate/core';

import { Inject, Injectable, OnDestroy } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  Subject,
  startWith,
  takeUntil,
} from 'rxjs';

import { MwUserService } from '@core/auth';
import { MwNotificationService, MW_APP_CONFIG } from '@core/services';
import { Config } from '@core/config';

import { UserProfileUpdateDto } from './@models';
import { LanguageViewModel } from './@models/language-view.model';
import { UserProfileApiService } from './user-profile-api.service';
import { UserProfileForm } from './user-profile.form';

@Injectable()
export class UserProfileDataService implements OnDestroy {
  get languages$(): Observable<LanguageViewModel[]> {
    return this._languages$.asObservable();
  }

  private _languages$ = new BehaviorSubject<LanguageViewModel[]>([]);
  private _unsubscribeAll$ = new Subject<void>();
  constructor(
    private readonly translateService: TranslateService,
    private readonly apiService: UserProfileApiService,
    private readonly form: UserProfileForm,
    private readonly userService: MwUserService,
    private readonly notification: MwNotificationService,
    @Inject(MW_APP_CONFIG) private readonly config: Config
  ) {}

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  setupData(): void {
    this.setupLanguages();
  }

  save(dto: UserProfileUpdateDto): void {
    this.apiService.update(dto).subscribe((result) => {
      const language = result?.userLanguage?.toLocaleLowerCase() ?? 'en';
      this.form.setData({ ...result, userLanguage: language });
      this.userService.refreshUserProfile();
      this.userService.changeLanguage(language);
      this.translateService.use(language);
      this.translateService
        .get('CORE.PAGE.USER_PROFILE.save_success')
        .subscribe((text) => {
          this.notification.success(text);
        });
    });
  }

  unlink(): Observable<boolean> {
    return this.apiService.unlink();
  }

  link(): Observable<boolean> {
    return this.apiService.link();
  }

  private setupLanguages(): void {
    const languages = ['en', 'no'];
    const exportModuleClientId = 'MW-Client-Payroll-Batch';
    if (this.config.environment.clientId === exportModuleClientId) {
      languages.push('se');
    }

    this.translateService.onLangChange
      .pipe(
        startWith(this.translateService.currentLang),
        takeUntil(this._unsubscribeAll$)
      )
      .subscribe(() => {
        this._languages$.next(
          languages.map(
            (lang) =>
              ({
                title: this.translateService.instant(
                  `CORE.PAGE.USER_PROFILE.language.${lang}`
                ),
                value: lang,
              } as LanguageViewModel)
          )
        );
      });
  }
}
